// Florida State University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - FSU's Common Data Set 2025-2026, published as a web page by the Office
//    of Institutional Research (pick the year and section on the page; no
//    PDF): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table, C8 test policy for Fall 2027, C14/C16 dates, C21/C22
//    plans, G1/G5 2026-2027 costs (not marked as unavailable). C21 gives
//    no early decision counts, so there is no early round rate.
//  - admissions.fsu.edu: First-Year Apply (application plans, the Early
//    Decision questions and answers, talent-based majors, Honors), Deadlines
//    & Application Fees, and What We're Looking For (testing). The deadline
//    table carries no year; the CDS gives the same dates with years for the
//    fall 2027 cycle (October 15, 2026 early plans, December 1, 2026 priority,
//    March 1, 2027 closing). Quirk: the CDS says Regular Decision
//    notifications go out by 2/12/27, the Admissions page says February 18;
//    the page uses the Admissions date.
//  - College Scorecard, school id 134097 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'FSU Common Data Set 2025-2026',
        url: 'https://ir.fsu.edu/commondataset.aspx',
        read: '2026-10-01',
    },
    apply: {
        label: 'FSU Admissions, First-Year: Apply',
        url: 'https://admissions.fsu.edu/first-year/apply',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'FSU Admissions, Deadlines and Application Fees',
        url: 'https://admissions.fsu.edu/deadlines',
        read: '2026-10-01',
    },
    criteria: {
        label: 'FSU Admissions, What We\'re Looking For',
        url: 'https://admissions.fsu.edu/first-year/wwlf',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?134097-Florida-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'florida-state-university',
    kbId: 'florida_state_university',
    name: 'Florida State University',
    shortName: 'Florida State',
    aliases: ['FSU'],
    location: 'Tallahassee, FL',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (FSU brand visual style); decoration only.
    branding: {
        primary: '#782F40',
        secondary: '#CEB888',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/florida_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 85469,
        admitted: 20364,
        enrolled: 6559,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 44631, admitted: 15196 },
            { group: 'Out-of-state', applied: 34530, admitted: 4671 },
            { group: 'International', applied: 6308, admitted: 497 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'FSU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'FSU offers binding Early Decision to domestic applicants and non-binding Early Action to Florida residents. The Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to FSU through one application. Some majors and programs ask for more:',
        rows: [
            { school: 'Talent-based majors', requirement: 'If you plan to major in dance, film (animation or production), music, stage management, studio art or theatre, contact the academic department directly for its own major requirements. Talent-based programs cannot be applied to through Early Decision.' },
            { school: 'University Honors Program', requirement: 'Answer Yes to the Honors question on the application, then complete the separate Honors/Presidential Scholars application on the Admissions Portal by December 1.' },
            { school: 'CARE Summer Bridge Program', requirement: 'Answer Yes to the CARE question on the application and complete the separate CARE supplemental application on the Admissions Portal. CARE applicants cannot apply Early Decision.' },
        ],
        note: 'Every applicant self-reports courses and grades through STARS, uploads an essay and a resume or activity list. Letters of recommendation are not required and are not used.',
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'December 1' },
        rows: [
            { plan: 'Early Decision (domestic applicants)', deadline: 'October 15', decision: 'December 17', binding: true },
            { plan: 'Early Action (Florida residents only)', deadline: 'October 15', decision: 'December 17', binding: false },
            { plan: 'Regular Decision', deadline: 'December 1', decision: 'February 18', binding: false },
            { plan: 'Rolling', deadline: 'March 1', decision: 'Rolling basis in April', binding: false },
        ],
        note: 'Supporting materials are due a week after each application deadline: October 22, December 8 and March 8. Early Decision applicants must apply through the Common App. The deposit is due January 15 for Early Decision and May 1 for every other plan. The application fee is $30.',
        source: 'deadlines',
        binding: 'Early Decision is binding: if admitted, you withdraw all other college applications and pay the enrollment deposit by January 15. You may still apply elsewhere through non-binding Early Action, Regular Decision or rolling admission, but not to another binding plan. You, a parent or guardian, and a school counselor sign the Early Decision Agreement. If you are deferred, you are released from the agreement and hear on February 18. International applicants apply through Regular Decision.',
        bindingSource: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'FSU requires a test score: at least one ACT, CLT or SAT score must be submitted before the materials deadline. You enter your own scores on the Admissions Portal; FSU does not take scores entered in the Common App. FSU superscores the ACT, CLT and SAT using your highest section scores from every test date, and the ACT composite uses only English, math and reading.',
        source: 'criteria',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants, with scores due by March 1, 2027.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [4640, 23825] },
            { item: 'Required fees', amounts: [1877, 1877] },
            { item: 'Housing (on campus)', amounts: [7890, 7890] },
            { item: 'Food (on-campus meal plan)', amounts: [5584, 5584] },
            { item: 'Books and supplies', amounts: [1380, 1380] },
            { item: 'Transportation', amounts: [2320, 2320] },
            { item: 'Other expenses', amounts: [2504, 2504] },
        ],
        totals: [26195, 45380],
        note: 'For a full-time first-year student living on campus, fall and spring.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 8605,
            byIncome: [
                { income: '$0 to $30,000', amount: 4332 },
                { income: '$30,001 to $48,000', amount: 4290 },
                { income: '$48,001 to $75,000', amount: 6953 },
                { income: '$75,001 to $110,000', amount: 11367 },
                { income: 'Over $110,000', amount: 17085 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from FSU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
